import { Directive, ElementRef, Input, OnChanges, SimpleChanges, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Mapping from `:shortcode:` keys to Font Awesome icon classes (fas).
 * Usage in markdown strings: `:pen:`, `:info:`, etc.
 */
const ICON_MAP: Record<string, string> = {
  'pen':              'fa-pencil-alt',
  'pencil':           'fa-pencil-alt',
  'edit':             'fa-edit',
  'save':             'fa-save',
  'trash':            'fa-trash',
  'delete':           'fa-trash',
  'plus':             'fa-plus',
  'minus':            'fa-minus',
  'check':            'fa-check',
  'times':            'fa-times',
  'search':           'fa-search',
  'info':             'fa-info-circle',
  'question':         'fa-question-circle',
  'exclamation':      'fa-exclamation-circle',
  'warning':          'fa-exclamation-triangle',
  'eye':              'fa-eye',
  'eye-slash':        'fa-eye-slash',
  'lock':             'fa-lock',
  'unlock':           'fa-unlock',
  'cog':              'fa-cog',
  'download':         'fa-download',
  'upload':           'fa-upload',
  'link':             'fa-link',
  'chevron-right':    'fa-chevron-right',
  'chevron-left':     'fa-chevron-left',
  'chevron-up':       'fa-chevron-up',
  'chevron-down':     'fa-chevron-down',
  'arrow-right':      'fa-arrow-right',
  'arrow-left':       'fa-arrow-left',
  'filter':           'fa-filter',
  'sort':             'fa-sort',
  'copy':             'fa-copy',
  'print':            'fa-print',
  'home':             'fa-home',
  'user':             'fa-user',
  'users':            'fa-users',
  'calendar':         'fa-calendar',
  'clock':            'fa-clock',
  'star':             'fa-star',
  'heart':            'fa-heart',
  'tag':              'fa-tag',
  'folder':           'fa-folder',
  'file':             'fa-file',
};

@Directive({
  selector: '[markdown]'
})
export class MarkdownDirective implements OnChanges {
  @Input() markdown: string;
  @Input() markdownInline: boolean = false;

  private _cachedMarkdown: string | null = null;
  private _cachedHtml: string | null = null;

  constructor(
    private el: ElementRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markdown']) {
      this.render();
    }
  }

  private render(): void {
    if (!this.markdown) {
      this.el.nativeElement.innerHTML = '';
      this._cachedMarkdown = null;
      this._cachedHtml = null;
      return;
    }

    if (this.markdown === this._cachedMarkdown) {
      this.el.nativeElement.innerHTML = this._cachedHtml;
      return;
    }

    const html = this.markdownInline ? this.inlineMarkdownToHtml(this.markdown) : this.markdownToHtml(this.markdown);
    // Sanitize HTML to prevent XSS attacks
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);
    this._cachedHtml = sanitized || '';
    this._cachedMarkdown = this.markdown;
    this.el.nativeElement.innerHTML = this._cachedHtml;
  }

  private inlineMarkdownToHtml(markdown: string): string {
    let html = markdown;

    // Escape HTML special characters first
    html = this.escapeHtml(html);

    // Process inline code (must be before bold/italic to avoid conflicts)
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Process bold (** or __)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Process italics (* or _) - must be after bold
    html = html.replace(/\*([^\*]+?)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+?)_/g, '<em>$1</em>');

    // Convert line breaks to <br> (no paragraph wrapping in inline mode)
    html = html.replace(/\n/g, '<br>');

    // Resolve icon shortcodes
    html = this.processIconShortcodes(html);

    return html;
  }

  private markdownToHtml(markdown: string): string {
    let html = markdown;

    // Escape HTML special characters first (except those we'll use for markdown)
    html = this.escapeHtml(html);

    // Process fenced code blocks (must be before inline code)
    html = this.processFencedCodeBlocks(html);

    // Process blockquotes
    html = this.processBlockquotes(html);

    // Process headings (h1-h6)
    html = this.processHeadings(html);

    // Process bold (** or __)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Process italics (* or _)
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Process inline code
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Process unordered lists
    html = this.processUnorderedLists(html);

    // Process ordered lists
    html = this.processOrderedLists(html);

    // Process paragraphs (double line breaks)
    html = this.processParagraphs(html);

    // Convert single line breaks to <br>
    html = html.replace(/\n/g, '<br>');

    // Resolve icon shortcodes
    html = this.processIconShortcodes(html);

    return html;
  }

  /**
   * Replaces :shortcode: tokens with Font Awesome <i> elements.
   * Must run after escapeHtml() (`:` is not escaped) and after bold/italic
   * processing so shortcodes can appear inside markdown formatting.
   */
  private processIconShortcodes(html: string): string {
    return html.replace(/:([a-z][a-z0-9-]*):/g, (match, key) => {
      const iconClass = ICON_MAP[key];
      if (!iconClass) {
        return match; // unknown shortcode — leave as-is
      }
      return `<i class="fas ${iconClass} fa-sm" aria-hidden="true"></i>`;
    });
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    // Only escape & when it is NOT already the start of a valid HTML entity
    // (named: &nbsp; | decimal: &#160; | hex: &#xA0;)
    let html = text.replace(/&(?![a-zA-Z]+;|#\d+;|#x[0-9a-fA-F]+;)/g, '&amp;');
    return html.replace(/[<>"']/g, m => map[m]);
  }

  private processFencedCodeBlocks(html: string): string {
    // Match ```language\ncode\n``` or ```\ncode\n```
    return html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
      const langClass = language ? ` class="language-${this.escapeHtml(language)}"` : '';
      return `<pre><code${langClass}>${code.trim()}</code></pre>`;
    });
  }

  private processBlockquotes(html: string): string {
    const lines = html.split('\n');
    let inBlockquote = false;
    let blockquoteContent: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      if (line.trim().startsWith('&gt; ')) {
        if (!inBlockquote) {
          inBlockquote = true;
          blockquoteContent = [];
        }
        blockquoteContent.push(line.trim().substring(5)); // Remove '> '
      } else {
        if (inBlockquote) {
          result.push(`<blockquote>${blockquoteContent.join('\n')}</blockquote>`);
          inBlockquote = false;
          blockquoteContent = [];
        }
        result.push(line);
      }
    }

    // Handle case where blockquote is at the end
    if (inBlockquote) {
      result.push(`<blockquote>${blockquoteContent.join('\n')}</blockquote>`);
    }

    return result.join('\n');
  }

  private processHeadings(html: string): string {
    // H6 to H1 (process in reverse order to handle longer matches first)
    html = html.replace(/^#{6}\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#{5}\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>');
    return html;
  }

  private processUnorderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    let listItems: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      const match = line.match(/^[\*\-\+]\s+(.+)$/);
      if (match) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(`<li>${match[1]}</li>`);
      } else {
        if (inList) {
          result.push(`<ul>${listItems.join('')}</ul>`);
          inList = false;
          listItems = [];
        }
        result.push(line);
      }
    }

    // Handle case where list is at the end
    if (inList) {
      result.push(`<ul>${listItems.join('')}</ul>`);
    }

    return result.join('\n');
  }

  private processOrderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    let listItems: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      const match = line.match(/^\d+\.\s+(.+)$/);
      if (match) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(`<li>${match[1]}</li>`);
      } else {
        if (inList) {
          result.push(`<ol>${listItems.join('')}</ol>`);
          inList = false;
          listItems = [];
        }
        result.push(line);
      }
    }

    // Handle case where list is at the end
    if (inList) {
      result.push(`<ol>${listItems.join('')}</ol>`);
    }

    return result.join('\n');
  }

  private processParagraphs(html: string): string {
    // Split by double line breaks
    const blocks = html.split(/\n\n+/);
    
    return blocks.map(block => {
      block = block.trim();
      
      // Don't wrap if already wrapped in a block element
      if (
        block.startsWith('<h') ||
        block.startsWith('<ul>') ||
        block.startsWith('<ol>') ||
        block.startsWith('<pre>') ||
        block.startsWith('<blockquote>')
      ) {
        return block;
      }
      
      // Don't wrap empty blocks
      if (!block) {
        return '';
      }
      
      return `<p>${block}</p>`;
    }).join('');
  }
}
